"use client";

import { useRef, useCallback, useEffect } from "react";
import { LANDING_CONTAINER_CLASS } from "@/components/landing/constants";
import { Button, LoadingImage } from "@restomat/components/client/ui";
import { CheckCircle2 } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { motion, useInView } from "framer-motion";
import { fadeInUp, staggerContainer, cardReveal } from "./animations";
import { templates } from "./template-data";

const SCROLL_SPEED = 1;
const SCROLL_BACK_SPEED = 6;

const platformFeatureKeys = [
  "restomatConnection",
  "automaticMenu",
  "events",
  "alaCarte",
  "gallery",
  "eventPopup",
  "contactForm",
  "responsiveDesign",
  "seoOptimization",
] as const;

function TemplateCard({
  id,
  titleKey,
  tagKeys,
}: {
  id: string;
  titleKey: string;
  tagKeys: string[];
}) {
  const t = useTranslations("landing.templatesShowcase");
  const tTemplates = useTranslations("landing.templates");
  const title = tTemplates(`${titleKey}.title`);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);

  const cancelAnimation = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  const startScroll = useCallback(() => {
    cancelAnimation();
    const scroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (el.scrollTop < maxScroll) {
        el.scrollTop += SCROLL_SPEED;
        animationRef.current = requestAnimationFrame(scroll);
      }
    };
    animationRef.current = requestAnimationFrame(scroll);
  }, [cancelAnimation]);

  const scrollBack = useCallback(() => {
    cancelAnimation();
    const back = () => {
      const el = containerRef.current;
      if (!el) return;
      if (el.scrollTop > 0) {
        el.scrollTop = Math.max(0, el.scrollTop - SCROLL_BACK_SPEED);
        animationRef.current = requestAnimationFrame(back);
      }
    };
    animationRef.current = requestAnimationFrame(back);
  }, [cancelAnimation]);

  useEffect(() => cancelAnimation, [cancelAnimation]);

  return (
    <Link
      href={`https://sablony.restomat.cz/${id}`}
      rel="noopener noreferrer"
      target="_blank"
      className="group block"
    >
      <div className="rounded-xl border-2 border-foreground/10 overflow-hidden transition-all duration-300 group-hover:border-foreground/30 group-hover:shadow-xl">
        <div
          ref={containerRef}
          className="h-[280px] md:h-[340px] 2xl:h-[420px] overflow-hidden"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          onMouseEnter={startScroll}
          onMouseLeave={scrollBack}
        >
          <LoadingImage
            src={`/img/landing/templates/${id}.webp`}
            alt={t("templateAlt", { title })}
            width={640}
            height={1554}
            className="w-full h-auto"
          />
        </div>
        <div className="p-4 space-y-2">
          <h3 className="font-primary text-lg font-bold">{title}</h3>
          <div className="flex flex-wrap gap-1.5">
            {tagKeys.map((tagKey) => (
              <span
                key={tagKey}
                className="text-xs bg-foreground/5 text-foreground/60 rounded-full px-2.5 py-0.5"
              >
                {tTemplates(`tags.${tagKey}`)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}

function HeroShapes() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      <svg
        className="absolute -top-20 -right-16 w-[320px] h-[320px] text-background/[0.04]"
        viewBox="0 0 200 200"
        fill="currentColor"
      >
        <path d="M44.7,-76.4C58.8,-69.2,71.8,-59.1,79.6,-45.8C87.4,-32.5,90,-16.3,88.5,-0.9C87,14.5,81.4,29,72.4,40.8C63.4,52.6,51,61.7,37.5,68.2C24,74.7,9.5,78.6,-4.3,78.1C-18.1,77.6,-31.2,72.7,-43.4,65.3C-55.6,57.9,-66.9,48,-74.1,35.5C-81.3,23,-84.4,7.9,-82.6,-6.3C-80.8,-20.5,-74.1,-33.8,-64.5,-44.3C-54.9,-54.8,-42.4,-62.5,-29.5,-70.3C-16.6,-78.1,-3.3,-86,7.8,-84.3C18.9,-82.6,30.6,-83.6,44.7,-76.4Z" transform="translate(100 100)" />
      </svg>
      <svg
        className="absolute -bottom-24 -left-12 w-[280px] h-[280px] text-secondary/[0.06]"
        viewBox="0 0 200 200"
        fill="currentColor"
      >
        <path d="M39.5,-65.3C50.9,-59.5,59.7,-48.6,66.8,-36.4C73.9,-24.2,79.3,-10.7,78.5,2.3C77.7,15.3,70.7,27.8,62.1,38.7C53.5,49.6,43.3,58.9,31.4,64.5C19.5,70.1,5.9,72,-8.3,71.6C-22.5,71.2,-37.3,68.5,-48.3,60.7C-59.3,52.9,-66.5,40,-71.2,26.1C-75.9,12.2,-78.1,-2.7,-75.1,-16.5C-72.1,-30.3,-63.9,-43,-52.5,-49.1C-41.1,-55.2,-26.5,-54.7,-13.5,-58.3C-0.5,-61.9,10.9,-69.6,22.3,-71.1C33.7,-72.6,28.1,-71.1,39.5,-65.3Z" transform="translate(100 100)" />
      </svg>
      <svg
        className="absolute top-1/2 right-8 w-[160px] h-[160px] text-background/[0.03]"
        viewBox="0 0 200 200"
        fill="currentColor"
      >
        <path d="M47.7,-79.1C62.3,-71.5,74.8,-59.3,82.3,-44.7C89.8,-30.1,92.3,-13.1,89.6,2.6C86.9,18.3,79,32.7,69,44.6C59,56.5,46.9,65.9,33.5,72.1C20.1,78.3,5.4,81.3,-9.3,79.7C-24,78.1,-38.7,71.9,-50.5,62.6C-62.3,53.3,-71.2,40.9,-76.5,27C-81.8,13.1,-83.5,-2.3,-80.1,-16.5C-76.7,-30.7,-68.2,-43.7,-56.6,-52.3C-45,-60.9,-30.3,-65.1,-16.1,-68.5C-1.9,-71.9,11.8,-74.5,25.2,-74.5C38.6,-74.5,33.1,-86.7,47.7,-79.1Z" transform="translate(100 100)" />
      </svg>
      <svg
        className="absolute top-1/3 -left-20 w-[200px] h-[200px] text-background/[0.03]"
        viewBox="0 0 200 200"
        fill="currentColor"
      >
        <path d="M41.3,-70.9C53.9,-63.4,64.8,-53.3,72.7,-40.9C80.6,-28.5,85.5,-13.8,84.6,0.5C83.7,14.8,77,30.2,67.5,42.3C58,54.4,45.7,63.2,32.4,69.3C19.1,75.4,4.8,78.8,-9.7,78.1C-24.2,77.4,-38.9,72.6,-50.3,64C-61.7,55.4,-69.8,43,-75.2,29.3C-80.6,15.6,-83.3,0.6,-80.8,-13.2C-78.3,-27,-70.6,-39.6,-60,-48.6C-49.4,-57.6,-35.9,-63,-23,-68.9C-10.1,-74.8,2.2,-81.2,15,-80.1C27.8,-79,28.7,-78.4,41.3,-70.9Z" transform="translate(100 100)" />
      </svg>
    </div>
  );
}

export default function TemplatesShowcase() {
  const t = useTranslations("landing.templatesShowcase");
  const tCta = useTranslations("landing.cta");
  const heroRef = useRef(null);
  const gridRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, amount: "some" });
  const gridInView = useInView(gridRef, { once: true, amount: "some" });

  return (
    <>
      {/* Hero section — compact */}
      <section
        ref={heroRef}
        className="relative border-b-4 border-black bg-foreground py-12 md:py-16 text-background"
      >
        <HeroShapes />
        <motion.div
          className={`${LANDING_CONTAINER_CLASS} relative z-10`}
          initial="hidden"
          animate={heroInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.div className="mx-auto text-center space-y-4" variants={fadeInUp}>
            <h1 className="text-[3rem] leading-[3.4rem] md:text-[6rem] lg:text-[7.5rem] md:leading-tight">
              {t("heading")}
            </h1>
            <p className="text-sm md:text-base text-background/70">
              {t("subheading")}
            </p>
          </motion.div>

          <motion.div
            className="flex flex-wrap justify-center gap-x-4 gap-y-2 max-w-xl mx-auto mt-5"
            variants={fadeInUp}
          >
            {platformFeatureKeys.map((key) => (
              <span
                key={key}
                className="flex items-center gap-1.5 text-xs md:text-sm text-background/80"
              >
                <CheckCircle2 className="text-secondary size-4 flex-shrink-0" />
                {t(`platformFeatures.${key}`)}
              </span>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* Templates grid */}
      <section ref={gridRef} className="py-12 md:py-16">
        <motion.div
          className={LANDING_CONTAINER_CLASS}
          initial="hidden"
          animate={gridInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
            {templates.map((template) => (
              <motion.div key={template.id} variants={cardReveal}>
                <TemplateCard
                  id={template.id}
                  titleKey={template.titleKey}
                  tagKeys={template.tagKeys}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="border-t-4 border-black bg-foreground py-10 text-background text-center">
        <div className={`${LANDING_CONTAINER_CLASS} space-y-4`}>
          <h2 className="text-xl md:text-2xl font-black">
            {t("ctaHeading")}
          </h2>
          <p className="text-background/60 text-sm md:text-base">
            {t("ctaSubheading")}
          </p>
          <Button variant="secondary" size="sm" asChild>
            <Link href="/auth/login?screen_hint=signup" prefetch={false}>
              {tCta("startFree")}
            </Link>
          </Button>
        </div>
      </section>
    </>
  );
}
